import { useNavigate } from 'react-router-dom';
import { agentDomains, maturityStages } from '../data/agents';
import { MultiAgent, ArrowLeft } from '../components/Icons';
import CodeBlock from '../components/CodeBlock';

export default function AgentSystemPage() {
  const navigate = useNavigate();

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="detail-header">
        <button className="back-link" onClick={() => navigate('/')}>
          <ArrowLeft /> Back to home
        </button>
        <div className="icon-large"><MultiAgent /></div>
        <h1>Multi-Agent <span style={{ color: 'var(--blue)' }}>System</span></h1>
        <div className="subtitle">21 agents × 124 skills across three DevOps domains</div>
      </div>

      {/* Stats */}
      <div className="stats-bar">
        <div className="stat-item"><div className="num" style={{ color: 'var(--blue)' }}>3</div><div className="label">Domains</div></div>
        <div className="stat-item"><div className="num" style={{ color: 'var(--violet)' }}>21</div><div className="label">Agents</div></div>
        <div className="stat-item"><div className="num" style={{ color: 'var(--green)' }}>124</div><div className="label">Skills</div></div>
        <div className="stat-item"><div className="num" style={{ color: 'var(--orange)' }}>7</div><div className="label">Workflows each</div></div>
      </div>

      {/* Architecture */}
      <div className="section-block">
        <h2><span className="section-bar" style={{ background: 'var(--blue)' }} />Architecture</h2>
        <ArchDiagram />
      </div>

      {/* Agent domains */}
      {Object.entries(agentDomains).map(([key, domain]) => (
        <div key={key} className="agent-domain">
          <div className="agent-domain-header">
            <span className="tag" style={{ color: domain.color, border: `1px solid ${domain.color}`, background: `${domain.color}10` }}>
              {domain.agents.length} agents
            </span>
            <h3 style={{ color: domain.color }}>{domain.name}</h3>
          </div>
          <div className="agent-list">
            {domain.agents.map((agent, i) => (
              <div key={i} className={`agent-item page-enter stagger-${(i % 7) + 1}`}>
                <h4 style={{ color: domain.color }}>{agent.name}</h4>
                <p>{agent.role}</p>
                <span className="skills-badge">{agent.skills} skills</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Workflow example */}
      <div className="section-block" style={{ marginTop: 40 }}>
        <h2><span className="section-bar" style={{ background: 'var(--violet)' }} />Example Workflow: IaC PR Review</h2>
        <CodeBlock
          title="infra_pr_review workflow"
          code={`Engineer opens PR with .tf changes
        │
  ┌─────▼──── PARALLEL ──────────────┐
  │                                   │
  │  Policy Guardian    Cost Sentinel │
  │   • scan_security   • estimate   │
  │   • evaluate_policy • enforce    │
  │   • secret_detect     _budget    │
  │                                   │
  └──────────────┬───────────────────┘
                 │
                 ▼
          Change Planner
           • analyze_plan
           • assess_risk
           • route_approval
           • generate_runbook
                 │
                 ▼
    PR Comment: unified report
    ✓ Security: 0 critical
    ✓ Cost: +$42/mo (within budget)
    ✓ Risk: Low (score 18/100)
    → Auto-approved for staging`}
        />
      </div>

      {/* Maturity */}
      <div className="section-block" style={{ marginTop: 40 }}>
        <h2><span className="section-bar" style={{ background: 'var(--green)' }} />Maturity Stages</h2>
        <div className="maturity-list">
          {maturityStages.map((s, i) => (
            <div key={i} className={`maturity-item page-enter stagger-${i + 1}`}>
              <span className="index" style={{ color: s.color }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <div>
                <div className="stage-name" style={{ color: s.color }}>{s.stage}</div>
                <div className="stage-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Architecture Diagram ──────────────────────────────────────────────── */

function ArchDiagram() {
  const box = (label, color) => ({
    style: {
      borderColor: color || 'var(--text-dim)',
      color: color || 'var(--text-muted)',
      background: color ? `${color}0d` : 'rgba(255,255,255,0.02)',
    },
    label,
  });

  const rows = [
    [box('Webhook / Trigger')],
    [box('FastAPI Gateway', 'var(--blue)')],
    [box('Orchestrator', 'var(--violet)')],
    [box('Agent A', 'var(--green)'), box('Agent B', 'var(--orange)'), box('Agent C', 'var(--pink)')],
    [box('Skills (LLM Calls)'), box('MCP Servers')],
  ];

  return (
    <div className="arch-diagram">
      {rows.map((row, ri) => (
        <div key={ri}>
          {ri > 0 && <div className="arch-arrow">↓</div>}
          <div className="arch-row">
            {row.map((b, bi) => (
              <div key={bi} className="arch-box" style={b.style}>{b.label}</div>
            ))}
          </div>
        </div>
      ))}
      <div className="arch-caption">
        Webhook → FastAPI → Orchestrator → Agent sequence → Skills (LLM) → MCP Servers (tools)
      </div>
    </div>
  );
}
